import { Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import React from 'react';

import EditOrFinishFreeze from './EditOrFinishFreeze';
import FreezeDetail from './FreezeDetail';

import type {
  MemberFreezeDetailResponse,
  MemberFreezeHistoryResponse,
  MemberFreezeResponse,
} from './types';
import moment, { type Moment } from 'moment';
import {
  type ErrorResponse,
  asyncErrorHandler,
} from '@/utils/asyncErrorHandler';

dayjs.extend(isSameOrAfter);

export const getColumns = (
  getTableData: () => void
): ColumnsType<MemberFreezeResponse> => [
  {
    title: '信箱',
    dataIndex: 'realEmail',
  },
  {
    title: '會員名',
    dataIndex: 'name',
  },
  {
    title: '手機號碼',
    dataIndex: 'phoneNumber',
  },
  {
    title: '計費方案名稱',
    dataIndex: 'planName',
  },
  {
    title: '會員期限(年/月/日)',
    dataIndex: 'expiration',
    render: (value) => {
      return dayjs(value).format('YYYY/MM/DD');
    },
  },
  {
    title: '狀態',
    dataIndex: 'status',
  },
  {
    title: '凍結剩餘次數',
    dataIndex: 'freezeCountLeft',
    render: (value) => value + '次',
  },
  {
    title: '凍結剩餘天數',
    dataIndex: 'freezeDurationLeft',
    render: (value) => dayjs(value).diff(dayjs(0), 'day') + '天',
  },
  {
    title: '凍結結束日期(年/月/日)',
    dataIndex: 'currentFreezeEndTime',
    render: (value) => {
      return value ? dayjs(value).format('YYYY/MM/DD') : null;
    },
  },
  {
    title: '操作',
    dataIndex: 'actions',
    width: 200,
    fixed: 'right',
    render: (_value, record) => {
      return (
        <Space>
          <FreezeDetail record={record} />
          <EditOrFinishFreeze
            record={record}
            getTableData={getTableData}
            isDeleteButton={false}
          />
          {record.status === '凍結中' && (
            <EditOrFinishFreeze
              record={record}
              getTableData={getTableData}
              isDeleteButton
            />
          )}
        </Space>
      );
    },
  },
];

const chineseNums = [
  '零',
  '一',
  '二',
  '三',
  '四',
  '五',
  '六',
  '七',
  '八',
  '九',
];

const chineseUnits = ['', '十', '百', '千'];

export const numberToChinese = (number: number) => {
  let result = '';

  const numString = (number + 1).toString();

  const length = numString.length;

  let hasZero = false;

  for (let i = 0; i < length; i++) {
    const digit = parseInt(numString[i]);
    const unit = length - i - 1;

    if (digit === 0) {
      hasZero = true;
      continue;
    }

    if (hasZero) {
      result += chineseNums[0];
      hasZero = false;
    }

    if (length === 2 && i === 0 && digit === 1) {
      result += chineseUnits[unit];
    } else {
      result += chineseNums[digit] + chineseUnits[unit];
    }
  }

  return result;
};

export const labelStyle = { style: { padding: 0 } };

export const getFreezeDetailData = async (userId: string, planId: string) => {
  try {
    const res = await axios({
      method: 'POST',
      url: '/ezcon/api/memberFreezeManagementDetail',
      headers: {
        authorization: localStorage.getItem('feathers-jwt'),
      },
      data: {
        _id: userId,
        subscriptionPlanId: planId,
      },
    });
    const data = await res.data;
    return data;
  } catch (error) {
    const errObj: ErrorResponse = error.response.data;
    asyncErrorHandler(errObj);
    return new Error(errObj.debugInfo);
  }
};

const checkIsDurationBetweenSelectedDuration = (args: {
  freezeHistory: MemberFreezeHistoryResponse[] | undefined;
  freezeStartTime: Moment;
  freezeEndTime: Moment | undefined;
}) => {
  const { freezeHistory, freezeStartTime, freezeEndTime } = args;

  const isDurationBetweenSelectedDuration = freezeHistory?.some(
    (duration) =>
      moment(duration.freezeStartTime).isBetween(
        freezeStartTime,
        freezeEndTime
      ) ||
      moment(duration.freezeEndTime).isBetween(freezeStartTime, freezeEndTime)
  );

  return isDurationBetweenSelectedDuration;
};

const checkIsRunOutOfRemainingTime = (args: {
  freezeDurationLeft: number;
  freezeStartTime: Moment;
  freezeEndTime: Moment;
}) => {
  const { freezeDurationLeft, freezeStartTime, freezeEndTime } = args;

  const isRunOutOfRemainingTime =
    freezeEndTime.diff(freezeStartTime, 'day') > freezeDurationLeft;

  return isRunOutOfRemainingTime;
};

export const startTimeValidator = async (args: {
  freezeDetail: MemberFreezeDetailResponse | undefined;
  freezeStartTime: Moment | null | undefined;
  freezeEndTime: Moment | undefined;
}) => {
  const { freezeDetail, freezeStartTime, freezeEndTime } = args;

  if (freezeStartTime === null || freezeStartTime === undefined) {
    return await Promise.reject(new Error('請選擇凍結開始日期'));
  }

  const isDurationBetweenSelectedDuration =
    checkIsDurationBetweenSelectedDuration({
      freezeHistory: freezeDetail?.freezeHistory,
      freezeStartTime,
      freezeEndTime,
    });

  if (isDurationBetweenSelectedDuration) {
    return await Promise.reject(new Error('選擇的期間與現有凍結期間重疊'));
  }

  if (freezeStartTime && freezeEndTime && freezeDetail?.freezeDurationLeft) {
    const isDurationOverDurationLeft = checkIsRunOutOfRemainingTime({
      freezeStartTime,
      freezeEndTime,
      freezeDurationLeft: dayjs(freezeDetail?.freezeDurationLeft).diff(
        dayjs(0),
        'day'
      ),
    });
    return isDurationOverDurationLeft
      ? await Promise.reject(new Error('選擇期間的天數超過剩餘天數'))
      : await Promise.resolve();
  }

  return await Promise.resolve();
};

export const endTimeValidator = (args: {
  freezeDetail: MemberFreezeDetailResponse | undefined;
  freezeStartTime: Moment | undefined;
  freezeEndTime: Moment;
}) => {
  const { freezeEndTime, freezeStartTime, freezeDetail } = args;

  if (freezeEndTime === null || freezeEndTime === undefined) {
    return Promise.reject(new Error('請選擇凍結結束日期'));
  }

  if (freezeStartTime === undefined) {
    return Promise.resolve();
  }

  if (
    freezeStartTime &&
    freezeEndTime.endOf('day').isAfter(freezeStartTime.startOf('day')) === false
  ) {
    return Promise.reject(new Error('結束日期不可早於開始日期'));
  }

  const isDurationBetweenSelectedDuration =
    checkIsDurationBetweenSelectedDuration({
      freezeHistory: freezeDetail?.freezeHistory,
      freezeStartTime,
      freezeEndTime: freezeEndTime,
    });

  if (isDurationBetweenSelectedDuration) {
    return Promise.reject(new Error('選擇的期間與現有凍結期間重疊'));
  }

  if (freezeStartTime && freezeEndTime && freezeDetail?.freezeDurationLeft) {
    const isDurationOverDurationLeft = checkIsRunOutOfRemainingTime({
      freezeStartTime,
      freezeEndTime,
      freezeDurationLeft: dayjs(freezeDetail?.freezeDurationLeft).diff(
        dayjs(0),
        'day'
      ),
    });
    return isDurationOverDurationLeft
      ? Promise.reject(new Error('選擇期間的天數超過剩餘天數'))
      : Promise.resolve();
  }

  return Promise.resolve();
};

export const checkDateIsAvailable = (args: {
  date: Moment;
  freezeHistory: MemberFreezeHistoryResponse[];
  historyIndex: number | undefined;
}) => {
  const { date, freezeHistory, historyIndex } = args;

  const isBefore = date.isBefore(moment(), 'day');
  const isBetween = freezeHistory.some((duration, index) => {
    if (historyIndex === index) {
      return false;
    }

    return date.isBetween(
      moment(duration.freezeStartTime).startOf('day'),
      moment(duration.freezeEndTime).endOf('day'),
      undefined,
      '[]'
    );
  });

  return isBefore || isBetween;
};

export const removeDurationFromArray = (
  durations: MemberFreezeHistoryResponse[] | undefined,
  removeValueIndex: number
) => {
  const newDurations = durations?.filter(
    (_duration, index) => index !== removeValueIndex
  );

  return newDurations;
};

export const getMemberFreezeList = (
  tableDataSource: MemberFreezeResponse[],
  phoneNumber: string,
  status: string
) => {
  let result = tableDataSource;
  if (phoneNumber) {
    result = result.filter((member) =>
      member.phoneNumber.includes(phoneNumber)
    );
  }
  if (status) {
    result = result.filter((member) => member.status.includes(status));
  }

  return result;
};

export const freezeStatusOptions = [
  {
    label: '凍結中',
    value: '凍結中',
  },
  {
    label: '使用中',
    value: '使用中',
  },
];
